// components/Peoples/guardian/StudentExitPeriods.tsx
import { useEffectiveStudentId } from "components/Peoples/guardian/hooks/useEffectiveStudentId";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useStudentExit from "../hooks/useStudentExit";
import CustomAlert from "components/Common/ui/CustomAlert";
import CustomButton from "components/Common/ui/CustomButton";
import Loader from "components/Common/loader";

interface StudentExitPeriodsProps {
  handleAddNew?: (period: string, lastSession: any) => void;
}

const StudentExitPeriods: React.FC<StudentExitPeriodsProps> = ({
  handleAddNew,
}) => {
  const { t } = useTranslation();
  const studentId = useEffectiveStudentId();
  const [periods, setPeriods] = useState({
    morning: { show: false, isPassed: false, lastSession: null },
    afternoon: { show: false, isPassed: false, lastSession: null },
    early: { show: false, isPassed: false, lastSession: null }
  });
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [creatingExit, setCreatingExit] = useState<string | null>(null);
  const { handleGetDailySchedulePeriods } = useStudentExit();

  // Fetch periods data
  const fetchPeriodsData = async () => {
    if (!studentId) return;

    setLoadingPeriods(true);
    try {
      const response = await handleGetDailySchedulePeriods(studentId);
      if (response && response.periods) {
        setPeriods({
          morning: {
            show: response.periods.morning?.show || false,
            isPassed: response.periods.morning?.isPassed || false,
            lastSession: response.periods.morning?.lastSession || null
          },
          afternoon: {
            show: response.periods.afternoon?.show || false,
            isPassed: response.periods.afternoon?.isPassed || false,
            lastSession: response.periods.afternoon?.lastSession || null
          },
          early: {
            show: response.periods.early?.show || false,
            isPassed: response.periods.early?.isPassed || false,
            lastSession: response.periods.early?.lastSession || null
          }
        });
      }
    } catch (error) {
      console.error("Error fetching periods:", error);
    } finally {
      setLoadingPeriods(false);
    }
  };

  // Handle period button click
  const handlePeriodClick = async (period: string, lastSession: any) => {
    if (!studentId) return;

    setCreatingExit(period);
    try {
      handleAddNew?.(period, lastSession);
    } catch (error) {
      console.error("Error creating student exit:", error);
    } finally {
      setCreatingExit(null);
    }
  };

  useEffect(() => {
    fetchPeriodsData();
  }, [studentId]);

  if (loadingPeriods) {
    return (
      <div className="student-exit-periods-container mb-4">
        <Loader />
      </div>
    );
  }

  // Check if any period is available (not passed and has classes)
  const hasAvailablePeriods =
    (periods.morning.show && !periods.morning.isPassed) ||
    (periods.afternoon.show && !periods.afternoon.isPassed) ||
    (periods.early.show && !periods.early.isPassed);

  // Check if all periods have passed
  const allPeriodsPassed =
    (periods.morning.show && periods.morning.isPassed) ||
    (periods.afternoon.show && periods.afternoon.isPassed) ||
    (periods.early.show && periods.early.isPassed);

  return (
    <div className="mb-4">
      <div className="row g-3">
        {/* Morning Button - Only show if available and not passed */}
        {periods.morning.show && !periods.morning.isPassed && (
          <div className="col-md-4">
            <CustomButton
              className="w-100 student-exit-period-button"
              onClick={() => handlePeriodClick('morning', periods.morning.lastSession)}
              variant="success"
              disabled={creatingExit !== null}
              size="lg"
            >
              {creatingExit === 'morning' ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" />
                  {t("creating")}
                </>
              ) : (
                <>
                  <i className="fas fa-sun me-2"></i>
                  {t("studentExit.exitType.morning")}
                </>
              )}
            </CustomButton>
          </div>
        )}

        {/* Afternoon Button - Only show if available and not passed */}
        {periods.afternoon.show && !periods.afternoon.isPassed && (
          <div className="col-md-4">
            <CustomButton
              className="w-100 student-exit-period-button"
              onClick={() => handlePeriodClick('afternoon', periods.afternoon.lastSession)}
              variant="warning"
              disabled={creatingExit !== null}
              size="lg"
            >
              {creatingExit === 'afternoon' ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" />
                  {t("creating")}
                </>
              ) : (
                <>
                  <i className="fas fa-cloud-sun me-2"></i>
                  {t("studentExit.exitType.afternoon")}
                </>
              )}
            </CustomButton>
          </div>
        )}

        {/* Early Button - Only show if available and not passed */}
        {periods.early.show && !periods.early.isPassed && (
          <div className="col-md-4">
            <CustomButton
              className="w-100 student-exit-period-button"
              onClick={() => handlePeriodClick('early', periods.early.lastSession)}
              variant="info"
              disabled={creatingExit !== null}
              size="lg"
            >
              {creatingExit === 'early' ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" />
                  {t("creating")}
                </>
              ) : (
                <>
                  <i className="fas fa-clock me-2"></i>
                  {t("studentExit.exitType.early")}
                </>
              )}
            </CustomButton>
          </div>
        )}

        {/* All periods passed message */}
        {allPeriodsPassed && hasAvailablePeriods === false && (
          <div className="col-12">
            <CustomAlert
              type="info"
              message={t("alert.all_sessions_passed.message")}
              description={t("alert.all_sessions_passed.description")}
            />
          </div>
        )}

        {/* No Classes Message */}
        {!hasAvailablePeriods && !allPeriodsPassed && (
          <div className="col-12">
            <CustomAlert
              type="warning"
              message={t("alert.no_classes_today.message")}
              description={t("alert.no_classes_today.description")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentExitPeriods;